import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  ApiError,
  structureRemapRefusal,
  type PendingStructureMapping,
  type SectionScopeAtRisk,
  type StructureAtRiskNode,
  type StructureRemapEntry,
  type StructureRemapSaved,
} from "@keydials/api-client";
import {
  buildStructureMap,
  carriedItems,
  describeHoldings,
  describeLeavesFeeds,
  describeSectionScopes,
  goesWithList,
  initialStructureSelection,
  moveCollisions,
  LIST_NOUN,
  refusalByRow,
  rowTitle,
  sameChoice,
  structureHeadline,
  structureRowId,
  suggestStructureMap,
  targetLabel,
  targetOptions,
  unansweredRows,
  visibleRows,
  type StructureChoice,
  type StructureNouns,
  type StructureSelection,
} from "./structureRemap";
import { DEFAULT_ITEM_NOUN, article } from "../vocabulary";

// KEY-807 (structure remap 5/5): the part of the upgrade notice (web-user), and of the Rollout tab's on-behalf mapping (web-admin), where the dial admin says what becomes of each list and item the
// new version removes while it still holds something. Written for whoever runs the dial, not an engineer: every node is named
// with what it holds, every choice is a sentence, and the server's own wording of a refusal is never shown.

const field: CSSProperties = {
  font: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: "var(--radius-sm, 6px)",
  border: "var(--border-w, 1px) solid var(--border)", background: "var(--surface)", color: "var(--text)", maxWidth: "100%",
};

/** The words a row's choices are put in, in the dial's own nouns: "Move into a list in the new version", "Keep as my own appointment". */
function actionWords(action: StructureChoice["action"], noun: string): string {
  if (action === "MoveTo") return `Move into ${article(noun)} ${noun} in the new version`;
  if (action === "KeepAsCustomUnder") return `Keep as my own ${noun}`;
  return "Let it go";
}

const isItem = (n: StructureAtRiskNode) => !!n.itemKey;

function choiceValue(c: StructureChoice | undefined): string {
  if (!c || c.action === "Discard") return "";
  return `${c.sectionKey}/${c.segmentKey ?? ""}/${c.action === "MoveTo" ? (c.itemKey ?? "") : ""}`;
}

export interface StructureRemapTableProps {
  mapping: PendingStructureMapping;
  droppedSectionScopes?: SectionScopeAtRisk[] | null;
  /** Saves the whole map (replacing any saved one) and says what it still leaves unanswered. */
  save: (entries: StructureRemapEntry[]) => Promise<StructureRemapSaved>;
  /** Called after a save went through, to re-read the notice (the hold may have lifted). */
  onSaved?: () => void | Promise<void>;
  /** Downloads a copy of what the update would clear (the notice's export-at-risk). */
  onExport?: () => void | Promise<void>;
  exportBusy?: boolean;
  /** The host app's button classes. web-user's (the default) are also what dial-workspace's own stylesheet defines. */
  buttonClasses?: { primary: string; secondary: string };
  /** The dial's own words for its lists and items. Left out, the table says "list" and "item". */
  nouns?: StructureNouns;
}

const KD_BUTTONS = { primary: "kd-btn-primary", secondary: "kd-btn-dashed" };

export function StructureRemapTable({ mapping, droppedSectionScopes, save, onSaved, onExport, exportBusy, buttonClasses = KD_BUTTONS, nouns }: StructureRemapTableProps) {
  const { atRisk, targets } = mapping;
  const suggestions = useMemo(() => suggestStructureMap(atRisk, targets), [atRisk, targets]);
  const [selection, setSelection] = useState<StructureSelection>(() => initialStructureSelection(atRisk, mapping.map, suggestions));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // A re-read (after a save, or a 409 that refreshed the notice) starts again from what is saved.
  useEffect(() => {
    setSelection(initialStructureSelection(atRisk, mapping.map, suggestions));
    setDirty(false);
  }, [atRisk, mapping.map, suggestions]);

  const savedSelection = useMemo(() => initialStructureSelection(atRisk, mapping.map, {}), [atRisk, mapping.map]);
  const rows = visibleRows(atRisk, selection);
  const unanswered = unansweredRows(atRisk, selection);
  const collisions = moveCollisions(atRisk, selection);
  const feedNodes = atRisk.filter((n) => n.leavesFeeds);
  const scopes = (droppedSectionScopes ?? []).filter((s) => s.subscriptionCount + s.grantCount + s.visibilityCount > 0);

  function choose(id: string, c: StructureChoice | null) {
    setSelection((prev) => {
      const next = { ...prev };
      if (c) next[id] = c;
      else delete next[id];
      return next;
    });
    setDirty(true);
    setSavedNote(null);
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function pickAction(n: StructureAtRiskNode, action: StructureChoice["action"]) {
    const id = structureRowId(n);
    if (action === "Discard") {
      setConfirmDiscard(id);
      return;
    }
    const current = selection[id];
    if (current?.action === action) return;
    // Start from the suggestion when it is this kind of choice, so switching back and forth doesn't lose it.
    const suggested = suggestions[id]?.choice;
    if (suggested?.action === action) choose(id, suggested);
    else if (action === "MoveTo") choose(id, { action: "MoveTo", sectionKey: "", segmentKey: "" });
    else choose(id, { action: "KeepAsCustomUnder", sectionKey: "", segmentKey: null, newName: null });
  }

  function pickTarget(n: StructureAtRiskNode, value: string) {
    const id = structureRowId(n);
    const current = selection[id];
    if (!current || current.action === "Discard") return;
    const [sectionKey, segmentKey, itemKey] = value.split("/");
    if (current.action === "MoveTo") choose(id, { action: "MoveTo", sectionKey, segmentKey, itemKey: itemKey || null });
    else choose(id, { ...current, sectionKey, segmentKey: segmentKey || null });
  }

  async function submit() {
    if (Object.keys(collisions).length > 0) return;
    setBusy(true);
    setError(null);
    setRowErrors({});
    try {
      const result = await save(buildStructureMap(atRisk, selection));
      setDirty(false);
      setSavedNote(result.missing.length === 0
        ? "Saved. Everything has a place, so the update can go ahead."
        : `Saved. ${result.missing.length} still to choose before the update can go ahead.`);
      await onSaved?.();
    } catch (e) {
      const refusal = structureRemapRefusal(e);
      if (refusal) {
        const byRow = refusalByRow(refusal.problems);
        setRowErrors(byRow);
        const shown = new Set(rows.map(structureRowId));
        const elsewhere = Object.keys(byRow).filter((id) => !shown.has(id)).length;
        setError(elsewhere > 0
          ? "Some of your choices couldn't be saved. Nothing was changed. The dial may have changed since this was opened: reload the page and choose again."
          : "Some of your choices couldn't be saved. Nothing was changed. Each one that needs another look says why below.");
      } else {
        setError(e instanceof ApiError && e.status !== 500 ? e.message : "Your choices couldn't be saved. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const confirming = confirmDiscard ? atRisk.find((n) => structureRowId(n) === confirmDiscard) ?? null : null;

  return (
    <section
      aria-label="Where your lists and items go"
      style={{
        marginTop: 12, padding: "10px 12px", borderRadius: 8,
        border: "1px solid color-mix(in srgb, var(--st-risk, #c14257) 40%, var(--border))",
        background: "color-mix(in srgb, var(--st-risk, #c14257) 5%, transparent)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{structureHeadline(atRisk, nouns)}</div>
      <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
        For each one, choose: move it into the new version, keep it as your own, or let it go. We've suggested a place where one
        fits, but nothing is decided until you save.
      </p>

      {(scopes.length > 0 || feedNodes.length > 0) && (
        <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", flexDirection: "column", gap: 3 }}>
          {scopes.map((s) => (
            <li key={`scope-${s.sectionKey}`} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{describeSectionScopes(s)}</li>
          ))}
          {feedNodes.map((n) => (
            <li key={`feed-${structureRowId(n)}`} style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{describeLeavesFeeds(n)}</li>
          ))}
        </ul>
      )}

      <ul role="list" aria-label="Lists and items to place" style={{ listStyle: "none", padding: 0, margin: "10px 0 0", display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((n) => {
          const id = structureRowId(n);
          const c = selection[id];
          const listWord = (nouns?.list ?? LIST_NOUN).one;
          const items = nouns?.itemFor?.(n.sectionKey) ?? DEFAULT_ITEM_NOUN;
          // The row's own noun, and the noun of what it is kept in (an item is kept in a list, a list in a section).
          const ownWord = isItem(n) ? items.one : listWord;
          const keptInWord = isItem(n) ? listWord : "section";
          const title = rowTitle(n, atRisk);
          const options = c && c.action !== "Discard" ? targetOptions(targets, n, c.action) : [];
          const value = choiceValue(c);
          const placeholder = !options.some((o) => o.value === value);
          const nested = isItem(n) && atRisk.some((l) => !l.itemKey && l.sectionKey === n.sectionKey && l.segmentKey === n.segmentKey);
          const carries = isItem(n) ? [] : carriedItems(n, atRisk);
          const parentGoes = !c && goesWithList(n, selection);
          const parentLabel = parentGoes ? atRisk.find((l) => !l.itemKey && l.sectionKey === n.sectionKey && l.segmentKey === n.segmentKey)?.label : null;
          const suggested = suggestions[id];
          const showSuggested = !!suggested && sameChoice(c, suggested.choice) && !sameChoice(c, savedSelection[id]);
          const rowError = rowErrors[id] ?? (collisions[id] ? `'${collisions[id]}' is already moving there. Choose a different place, or keep it as your own.` : null);
          const target = c ? targetLabel(targets, c) : null;
          return (
            <li
              key={id}
              data-row={id}
              style={{
                marginLeft: nested ? 18 : 0, padding: "8px 10px", borderRadius: 8,
                border: `1px solid ${rowError ? "var(--st-risk, #c14257)" : "var(--border)"}`, background: "var(--surface)",
              }}
            >
              <div style={{ fontSize: 13, color: "var(--text)" }}>
                <b>{title}</b>
                <span style={{ color: "var(--text-muted)" }}> · {describeHoldings(n, atRisk, nouns)}</span>
                {showSuggested && (
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: "var(--accent, #216e8b)" }}>Suggested</span>
                )}
              </div>
              {carries.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  Moving or keeping it brings its {carries.length === 1 ? items.one : `${carries.length} ${items.many}`} with it.
                </div>
              )}
              {parentGoes && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  Goes with '{parentLabel}' unless you choose otherwise.
                </div>
              )}

              <div role="radiogroup" aria-label={`What happens to ${n.label}`} style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
                {(["MoveTo", "KeepAsCustomUnder", "Discard"] as const).map((a) => (
                  <label key={a} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "var(--text)", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name={`structure-${id}`}
                      checked={c?.action === a}
                      disabled={busy}
                      onChange={() => pickAction(n, a)}
                    />
                    {actionWords(a, ownWord)}
                  </label>
                ))}
              </div>

              {c && c.action !== "Discard" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
                  <select
                    aria-label={c.action === "MoveTo" ? `Move ${n.label} into` : `Keep ${n.label} under`}
                    value={placeholder ? "//" : value}
                    disabled={busy}
                    onChange={(e) => pickTarget(n, e.target.value)}
                    style={field}
                  >
                    {/* Only while nothing is chosen: a suggestion pre-selects its target, which is then what shows. */}
                    {placeholder && (
                      <option value="//" disabled>
                        {c.action === "MoveTo"
                          ? `Choose ${article(ownWord)} ${ownWord}…`
                          : `Choose ${article(keptInWord)} ${keptInWord} to keep it in…`}
                      </option>
                    )}
                    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {c.action === "KeepAsCustomUnder" && (
                    <input
                      aria-label={`New name for ${n.label}`}
                      placeholder={`Name (leave blank to keep '${n.label}')`}
                      value={c.newName ?? ""}
                      disabled={busy}
                      onChange={(e) => choose(id, { ...c, newName: e.target.value })}
                      style={{ ...field, minWidth: 200 }}
                    />
                  )}
                </div>
              )}
              {c && c.action !== "Discard" && target && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                  {c.action === "MoveTo"
                    ? `Everything in it joins ${target}.`
                    : `Kept as your own ${ownWord}${c.newName?.trim() ? ` called '${c.newName.trim()}'` : ""} in ${target}.`}
                </div>
              )}
              {c?.action === "Discard" && (
                <div style={{ fontSize: 12, color: "var(--st-risk, #c14257)", marginTop: 4 }}>
                  This will be lost when the update is applied.
                </div>
              )}

              {confirmDiscard === id && confirming && (
                <div
                  role="alertdialog"
                  aria-label={`Let ${n.label} go?`}
                  style={{
                    marginTop: 8, padding: "8px 10px", borderRadius: 8,
                    border: "1px solid color-mix(in srgb, var(--st-risk, #c14257) 55%, var(--border))",
                    background: "color-mix(in srgb, var(--st-risk, #c14257) 8%, transparent)",
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--st-risk, #c14257)" }}>Let '{n.label}' go?</div>
                  <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
                    {describeHoldings(n, atRisk, nouns)}
                    {carries.length > 0 ? `, and what's inside it that you don't choose a place for,` : ""} will be lost when the update
                    is applied. It can't be undone. You can download a copy first.
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    {onExport && (
                      <button type="button" className={buttonClasses.secondary} style={{ fontSize: 12.5 }} disabled={exportBusy} onClick={() => void onExport()}>
                        {exportBusy ? "Preparing…" : "Download a copy"}
                      </button>
                    )}
                    <button type="button" className={buttonClasses.primary} style={{ fontSize: 12.5 }} onClick={() => { choose(id, { action: "Discard" }); setConfirmDiscard(null); }}>
                      Yes, let it go
                    </button>
                    <button type="button" className={buttonClasses.secondary} style={{ fontSize: 12.5 }} onClick={() => setConfirmDiscard(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {rowError && <div role="alert" style={{ fontSize: 12, color: "var(--st-risk, #c14257)", marginTop: 4 }}>{rowError}</div>}
            </li>
          );
        })}
      </ul>

      {error && <p role="alert" style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--st-risk, #c14257)" }}>{error}</p>}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
        <button type="button" className={buttonClasses.primary} style={{ fontSize: 13 }} disabled={busy || Object.keys(collisions).length > 0} onClick={() => void submit()}>
          {busy ? "Saving…" : "Save my choices"}
        </button>
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          {unanswered.length > 0 ? `${unanswered.length} still to choose` : "Everything has a place"}
          {dirty ? " · not saved yet" : ""}
        </span>
        {savedNote && !dirty && <span role="status" style={{ fontSize: 12.5, color: "var(--st-done, #2e8b57)" }}>{savedNote}</span>}
      </div>
    </section>
  );
}
